/* eslint-disable @typescript-eslint/no-extraneous-class -- needed*/

import { NoCodeDependencyKeys } from '@unifyapps/carbon/no-code/dependency/const';
import type { BlockId } from '@unifyapps/defs/types/block';
import type { DeviceVariantType } from '@unifyapps/defs/types/deviceVariant';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import type {
  DevicesOverrides,
  InterfacePageEntity,
  PageFieldDependency,
} from '@unifyapps/defs/types/page';
import type { Delta } from '@unifyapps/defs/types/page/deviceOverrides';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import type { BuildDependencyGraphItem } from '../../dependency/utils/buildDependencyGraph';
import type { DynamicAndConditionalPathHandler } from '../context/DependencyGraphProvider/handlers';
import InterfaceHelper from '../stores/InterfaceStore/helper/InterfaceHelper';

class InterfacePageHelper {
  static entity = 'e_component';

  static getName(interfacePage: InterfacePageEntity) {
    return interfacePage.properties.name;
  }

  static getPageSlug(interfacePage: InterfacePageEntity) {
    return interfacePage.properties.slug;
  }

  static getDocumentTitle(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.documentTitle;
  }

  static getId(interfacePage: InterfacePageEntity) {
    return interfacePage.id;
  }

  static getDevices(interfacePage: InterfacePageEntity) {
    return interfacePage.properties.devices;
  }

  static getFlags(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.flags;
  }

  static getDeviceBlocks(interfacePage: InterfacePageEntity, device: DeviceVariantType) {
    return interfacePage.properties.devices?.[device]?.blocks;
  }

  static getDeviceBlock(
    interfacePage: InterfacePageEntity,
    device: DeviceVariantType,
    blockId: BlockId,
  ) {
    if (!blockId) {
      return undefined;
    }

    return interfacePage.properties.devices?.[device]?.blocks?.[blockId];
  }

  static getEvents(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.events;
  }

  static getBaseDeviceBlocks(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.blocks;
  }

  static getPageFunctions(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.pageFunctions;
  }

  static getPageFunctionById({
    id,
    interfacePage,
  }: {
    interfacePage?: InterfacePageEntity;
    id: string;
  }) {
    return interfacePage?.properties.pageFunctions?.[id];
  }

  static getPageVariables(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.pageVariables;
  }

  static getPageFolders(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.folders;
  }

  static getFieldDependencies(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.dependencies;
  }

  static getDependencyDependsOn(fieldDependency: PageFieldDependency) {
    return fieldDependency.dpOn;
  }

  static extractDocumentTitleDependencyKeys(
    dep?: BuildDependencyGraphItem<DynamicAndConditionalPathHandler>,
  ): PageFieldDependency {
    return {
      [NoCodeDependencyKeys.DependsOn]: DependencyHelper.getDependsOn(dep),
      [NoCodeDependencyKeys.ConditionalPaths]: DependencyHelper.getConditionalPaths(dep),
      [NoCodeDependencyKeys.DynamicPaths]: DependencyHelper.getDynamicPaths(dep),
    };
  }

  static getPageVariableById({
    id,
    interfacePage,
  }: {
    interfacePage?: InterfacePageEntity;
    id: string;
  }) {
    return interfacePage?.properties.pageVariables?.[id];
  }

  static getExportLayout(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.exportLayout;
  }

  static getLoader(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.loader;
  }

  // single-source-of-truth accessor for the page's input-schema property map.
  // Consumers used to drill `interfacePage?.properties.inputSchema?.schema?.properties`
  // inline; centralizing it avoids re-typing the optional-chain everywhere a
  // caller wants per-key defaults / type info from the page's declared inputs.
  static getInputSchemaProperties(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.inputSchema?.schema?.properties;
  }

  static getBaseDeviceBlock(interfacePage?: InterfacePageEntity, blockId?: BlockId) {
    if (!blockId) {
      return undefined;
    }
    return interfacePage?.properties.blocks?.[blockId];
  }

  static getMetadata(interfacePage: InterfacePageEntity) {
    return interfacePage.properties.metadata;
  }

  static getBlockFromInterfacePage(
    interfacePage: InterfacePageEntity,
    device: DeviceVariantType,
    blockId: BlockId,
  ) {
    return (
      InterfacePageHelper.getDeviceBlock(interfacePage, device, blockId) ??
      InterfacePageHelper.getBaseDeviceBlock(interfacePage, blockId)
    );
  }

  static getBlockDiffInDevice(
    interfacePage: InterfacePageEntity,
    device: DeviceVariantType,
    blockId: BlockId,
  ): Delta | undefined {
    if (!blockId) {
      return undefined;
    }

    return interfacePage.properties.devices?.[device]?.diffs?.blocks?.[blockId];
  }

  static getFolderById({ id, interfacePage }: { interfacePage?: InterfacePageEntity; id: string }) {
    return interfacePage?.properties.folders?.[id];
  }

  static getDeviceOverrides(
    interfacePage: InterfacePageEntity,
    device: DeviceVariantType,
  ): DevicesOverrides['desktop'] {
    return interfacePage.properties.devices?.[device];
  }

  static getDeviceBlocksDiff(
    interfacePage: InterfacePageEntity,
    device: DeviceVariantType,
  ): Record<string, Delta> | undefined {
    return interfacePage.properties.devices?.[device]?.diffs?.blocks;
  }

  static getComponentType(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.componentType;
  }

  static getPageEvents(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.events;
  }

  static isPublicPage(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.publicAccess ?? false;
  }

  static getPageIdFromSlug(
    interfaceRecord: InterfaceEntity | null | undefined,
    slug: string,
    path?: string,
  ): string | undefined {
    if (!interfaceRecord || !slug) {
      return undefined;
    }

    if (path) {
      const resolvedPageId = InterfaceHelper.resolvePageIdFromPath(path, slug, interfaceRecord);

      if (resolvedPageId) {
        return resolvedPageId;
      }
    }

    const interfacePageIdVsSlugMap = InterfaceHelper.getInterfacePageIdVsSlugMap(interfaceRecord);

    const interfacePageId = Object.entries(interfacePageIdVsSlugMap).find(
      ([_, value]) => value === slug,
    )?.[0];

    return interfacePageId;
  }

  static isPagePublicBySlug(
    interfaceRecord: InterfaceEntity | null | undefined,
    slug: string,
    path?: string,
  ): boolean {
    if (!interfaceRecord || !slug) {
      return false;
    }

    const interfacePageId = InterfacePageHelper.getPageIdFromSlug(interfaceRecord, slug, path);

    if (!interfacePageId) {
      return false;
    }

    return InterfacePageHelper.isPagePublicById(interfaceRecord, interfacePageId);
  }

  static isPagePublicById(
    interfaceRecord: InterfaceEntity | null | undefined,
    pageId: string | undefined,
  ): boolean {
    if (!interfaceRecord || !pageId) {
      return false;
    }

    const publicPageIds = InterfaceHelper.getPublicPageIds(interfaceRecord);
    return publicPageIds?.includes(pageId) ?? false;
  }

  static getBuildDependencies(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.flags?.shouldUseBuiltDependencies;
  }

  static getPageVersion(interfacePage?: InterfacePageEntity) {
    return interfacePage?.properties.metadata?._version;
  }
}

export default InterfacePageHelper;
